import React, { useRef } from 'react';
import classNames from 'classnames';

import MetricBlock from 'containers/user/goals/goals-monitoring/monitoring-chart/components/metric-block';
import PlaceholderBlock from 'containers/user/goals/goals-monitoring/monitoring-chart/components/placeholder-block';

import Icons from 'assets/icons';

import type { CEBlock } from 'containers/user/goals/goals-monitoring/monitoring-chart/hooks/use-goals-monitoring';

const isRateMetric = (key: string): boolean =>
  key.toLowerCase().includes('rate');

interface CriticalElementChartProps {
  blocks?: CEBlock[];
  previousYear?: string | number;
  currentYear?: string | number;
  cohortName?: string;
  customClass?: string;
  chartId?: string;
  isRestricted?: boolean;
}

const CriticalElementChart = ({
  blocks = [],
  previousYear,
  currentYear,
  cohortName,
  customClass,
  chartId,
  isRestricted = false,
}: CriticalElementChartProps): React.JSX.Element | null => {
  const chartRef = useRef<HTMLDivElement>(null);

  if (!blocks.length) return null;

  return (
    <div
      id={chartId}
      className={classNames(
        'relative bg-white p-6 rounded-lg border border-navy-200',
        customClass,
      )}
      ref={chartRef}
    >
      <div className="flex gap-8">
        {blocks.map((block, index) => {
          const isPlaceholder = isRestricted && index < 2;

          if (isPlaceholder) {
            return (
              <div key={index} className="flex-1 max-w-[200px]">
                <PlaceholderBlock
                  metricKey={block.key}
                  previousYear={previousYear}
                  currentYear={currentYear}
                />
              </div>
            );
          }

          const isRate = isRateMetric(block.key);

          const scaleIfRate = (
            num: number | null | undefined,
          ): number | null | undefined => (isRate ? (num as number) * 100 : num);

          const primary = scaleIfRate(block.previous);
          const secondary = scaleIfRate(block.value);

          const metricConfig: {
            unit?: string;
            change: number | null | undefined;
            cohortChange: number | null | undefined;
          } = isRate
            ? {
                unit: '%',
                change: scaleIfRate(block.value),
                cohortChange: scaleIfRate(block.cohort),
              }
            : {
                change: block.percentageChange ?? null,
                cohortChange: block.cohortPercentageChange ?? null,
              };

          return (
            <div key={index} className="flex-1 max-w-[200px]">
              <MetricBlock
                metricKey={block.key}
                unit={metricConfig.unit}
                primary={primary}
                secondary={secondary}
                primaryLabel={previousYear}
                secondaryLabel={currentYear}
                change={metricConfig.change}
                cohortChange={metricConfig.cohortChange}
                cohortName={cohortName}
                isRate={isRate}
              />
            </div>
          );
        })}
      </div>
      {isRestricted && (
        <div className="flex items-center gap-2 px-3">
          <Icons.Warning
            customClass="size-4 shrink-0 text-amber-700"
            aria-hidden="true"
          />
          <span className="text-sm">
            This critical element is not available higher in the funnel.
          </span>
        </div>
      )}
    </div>
  );
};

export default CriticalElementChart;
